import { PropsWithChildren } from 'react';

import styled, { css, useTheme } from 'styled-components';

import CustomIcon, { CustomIconColor } from '@components/utils/CustomIcon';
import { AlertIcon, Info } from '@assets/icons';
import { GetSCProps, PaletteColor } from '@utils/types';

export const InfoNoteMessage = styled.span``;

export type InfoNoteContainerProps = GetSCProps<
  'backgroundColor' | 'color' | 'noMargin' | 'padding'
> & {
  variant?: 'hes' | 'generic' | 'warning' | 'verification';
  hideIcon?: boolean;
  customCss?: ReturnType<typeof css>;
};

const noteVariantMixin = ({ variant, theme }: InfoNoteContainerProps) => {
  switch (variant) {
    case 'verification':
      return css`
        outline: dashed 1px;
        background-color: ${theme?.palette.hesInfoBoxBackground};
        color: ${theme?.palette.fadedOrange};
      `;
    case 'hes':
      return css`
        outline: dashed 1px;
        min-height: 65px;
        background-color: ${theme?.palette.hesInfoBoxBackground};
        color: ${theme?.palette.fadedOrange};
      `;
    case 'warning':
      return css`
        background-color: ${theme?.palette.coral_01};
        color: ${theme?.palette.coral};
      `;
    default:
      return null;
  }
};

export const InfoNoteBox = styled.div<InfoNoteContainerProps>`
  display: flex;
  margin: ${({ noMargin }) => (noMargin ? undefined : '20px 0 42px')};
  padding: ${(p) => p.padding ?? '15px 20px 15px 15px'};
  border-radius: 2px;
  background-color: ${({ backgroundColor, theme: { palette } }) =>
    palette[backgroundColor as PaletteColor] ?? backgroundColor};
  color: ${({ theme: { palette }, color }) =>
    palette[color as PaletteColor] ?? color};
  ${InfoNoteMessage} {
    flex: 1;
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 0 ${(p) => (p.hideIcon ? 0 : 15)}px;
    line-height: normal;
    min-height: ${(p) => (p.variant === 'hes' ? '35px' : 'unset')};
  }
  ${noteVariantMixin}
  ${({ customCss }) => customCss};
  *[data-role='custom-icon'] {
    align-self: flex-start;
  }
`;

const getVariantIconColor = (variant: InfoNoteContainerProps['variant']) => {
  return variant === 'hes'
    ? ('fadedOrange' as const)
    : variant === 'warning'
    ? ('coral' as const)
    : undefined;
};
export const Note = ({
  children,
  color,
  backgroundColor,
  variant = 'generic',
  hideIcon,
  ...rest
}: PropsWithChildren<InfoNoteContainerProps>) => {
  const { palette } = useTheme();
  const containerProps = { color, paletteColor: color };
  const variantIconFill = getVariantIconColor(variant);
  const colorKey = variantIconFill ?? color;
  const fill = palette[colorKey as PaletteColor] ?? colorKey;
  const Icon = variant === 'warning' ? Info : AlertIcon;
  const iconProps: CustomIconColor = fill
    ? { fill }
    : { paletteColor: color as PaletteColor, color };
  return (
    <InfoNoteBox
      backgroundColor={backgroundColor}
      {...containerProps}
      {...rest}
      variant={variant}
      hideIcon={hideIcon}
    >
      {hideIcon ? null : (
        <CustomIcon icon={Icon} width={15} height={15} {...iconProps} />
      )}
      <InfoNoteMessage>{children}</InfoNoteMessage>
    </InfoNoteBox>
  );
};

export default Note;
